import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Onboarding } from '@ofself/shell';
import { useAuth } from '../../contexts/AuthContext';
import { joinNext, finishJoin, keptCodes, appForNext } from '../../utils/joinFlow';
import JoinFrame, { joinStages } from './JoinFrame';

// JOIN, STEP 5 — the recovery codes. These are the codes the first sign-in
// minted with the account's keys (AuthContext `pendingRecoveryCodes`), not the
// set register returned: that set is replaced at first login, so showing it
// handed people codes that no longer worked. Kept in the tab until saved, so a
// reload anywhere in the join does not lose them; then on to `next`, or home.
function JoinCodes() {
  const { pendingRecoveryCodes, clearPendingRecoveryCodes } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const next = joinNext(searchParams);
  const app = appForNext(next);
  const [copied, setCopied] = useState(false);

  const codes = useMemo(
    () => (pendingRecoveryCodes?.length ? pendingRecoveryCodes : keptCodes()),
    [pendingRecoveryCodes],
  );

  const leaving = useRef(false);
  const done = () => {
    leaving.current = true;
    finishJoin(next, navigate);
    clearPendingRecoveryCodes();
  };

  // Nothing to save (the account already had its keys): this step is not one.
  useEffect(() => {
    if (!codes.length && !leaving.current) { leaving.current = true; finishJoin(next, navigate); }
  }, [codes.length, next, navigate]);
  if (!codes.length) return null;

  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(codes.join('\n'));
      setCopied(true);
    } catch { /* the codes stay on screen to copy by hand */ }
  };

  return (
    <JoinFrame stages={joinStages('codes', next)}>
      <Onboarding.Step
        title="Your recovery codes"
        subtitle="If you forget your password, one of these gets you back in. Each works once, and they are shown only now: keep them somewhere safe."
        centered
      >
        <ol className="join-codes">
          {codes.map((code) => <li key={code}>{code}</li>)}
        </ol>
        <button type="button" className="join-copy" onClick={copyAll}>
          {copied ? 'Copied' : 'Copy all'}
        </button>
        <Onboarding.Next onClick={done}>
          {app ? `On to ${app.name}` : 'I saved these'}
        </Onboarding.Next>
      </Onboarding.Step>
    </JoinFrame>
  );
}

export default JoinCodes;
